/**
 * @file useElectricalApi.js
 * @description API composable for electrical hierarchy operations
 * Handles all HTTP requests related to electrical systems (switchboards, DDC panels, etc.)
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for electrical API operations
 * @param {number|Ref<number>} siteId (can be a ref or computed) - The site ID
 * @returns {Object} Electrical API methods and state
 */
export function useElectricalApi(siteId) {
  const $q = useQuasar();

  // State
  const loadingElectrical = ref(false);
  const saving = ref(false);
  const electricalData = ref([]);
  const switchboardTypes = ref([]);

  /**
   * Load electrical hierarchy for site
   * @returns {Promise<Array>} Electrical hierarchy data
   */
  async function loadElectricalHierarchy() {
    if (!siteId) {
      return [];
    }

    try {
      loadingElectrical.value = true;

      const result = await brickSchemaService.electrical.getHierarchy(siteId);

      if (!result.success) {
        throw new Error(result.error);
      }

      electricalData.value = result.data?.hierarchy || result.data || [];
      return electricalData.value;
    } catch (error) {
      electricalData.value = [];
      $q.notify({
        type: 'negative',
        message: `Failed to load electrical hierarchy: ${error.message}`,
      });
      return [];
    } finally {
      loadingElectrical.value = false;
    }
  }

  /**
   * Load switchboard types from BRICK Schema
   * @returns {Promise<Array>} Switchboard types
   */
  async function loadSwitchboardTypes() {
    try {
      const result = await brickSchemaService.electrical.getSwitchboardTypes();

      if (!result.success) {
        throw new Error(result.error);
      }

      switchboardTypes.value = result.data || [];
      return switchboardTypes.value;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load switchboard types from TTL file',
      });
      return [];
    }
  }

  /**
   * Create main switchboard (MSB)
   * @param {Object} msbData - MSB data to create
   * @returns {Promise<Object>} Created MSB
   */
  async function createMSB(msbData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.createMSB(toValue(siteId), msbData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newMSB = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `MSB "${msbData.name}" created successfully`,
      });

      return newMSB;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create MSB: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Create mechanical services switchboard (MSSB)
   * @param {Object} mssbData - MSSB data to create
   * @returns {Promise<Object>} Created MSSB
   */
  async function createMSSB(mssbData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.createMSSB(toValue(siteId), mssbData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newMSSB = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `MSSB "${mssbData.name}" created successfully`,
      });

      return newMSSB;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create MSSB: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Create DDC panel
   * @param {Object} ddcData - DDC panel data to create
   * @returns {Promise<Object>} Created DDC panel
   */
  async function createDDC(ddcData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.createDDC(toValue(siteId), ddcData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newDDC = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `DDC Panel "${ddcData.name}" created successfully`,
      });

      return newDDC;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create DDC panel: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Create new switchboard
   * @param {Object} switchboardData - Switchboard data to create
   * @returns {Promise<Object>} Created switchboard
   */
  async function createSwitchboard(switchboardData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.createSwitchboard(toValue(siteId), switchboardData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newSwitchboard = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Switchboard "${switchboardData.name}" created successfully`,
      });

      return newSwitchboard;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create switchboard: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update switchboard
   * @param {number} switchboardId - Switchboard ID
   * @param {Object} switchboardData - Updated switchboard data
   * @returns {Promise<Object>} Updated switchboard
   */
  async function updateSwitchboard(switchboardId, switchboardData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.updateSwitchboard(toValue(siteId), switchboardId, switchboardData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedSwitchboard = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Switchboard updated successfully',
      });

      return updatedSwitchboard;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update switchboard: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete switchboard/electrical node
   * @param {number} nodeId - Node ID to delete
   * @param {string} nodeType - Type of node (MSB, MSSB, DDC, DB, etc.)
   * @returns {Promise<void>}
   */
  async function deleteElectricalNode(nodeId, nodeType) {
    try {
      const result = await brickSchemaService.electrical.deleteNode(toValue(siteId), nodeType, nodeId);

      if (!result.success) {
        throw new Error(result.error);
      }

      $q.notify({
        type: 'positive',
        message: 'Electrical node deleted successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete electrical node: ${error.message}`,
      });
      throw error;
    }
  }

  /**
   * Allocate equipment to DDC controller
   * @param {Object} allocationData - DDC allocation data
   * @returns {Promise<Object>} Allocation result
   */
  async function allocateToDdc(allocationData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.allocateToDdc(toValue(siteId), allocationData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const allocation = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Equipment allocated to DDC successfully',
      });

      return allocation;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to allocate to DDC: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Save power connection
   * @param {Object} connectionData - Power connection data
   * @returns {Promise<Object>} Connection result
   */
  async function savePowerConnection(connectionData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.savePowerConnection(toValue(siteId), connectionData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const connection = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Power connection saved successfully',
      });

      return connection;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to save power connection: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Save switchboard classification
   * @param {number} switchboardId - Switchboard ID
   * @param {Object} classificationData - Classification data
   * @returns {Promise<Object>} Classification result
   */
  async function saveSwitchboardClassification(switchboardId, classificationData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.electrical.saveSwitchboardClassification(
        siteId,
        switchboardId,
        classificationData,
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      const classification = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Switchboard classification saved successfully',
      });

      return classification;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to save switchboard classification: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    loadingElectrical,
    saving,
    electricalData,
    switchboardTypes,

    // Methods
    loadElectricalHierarchy,
    loadSwitchboardTypes,
    createMSB,
    createMSSB,
    createDDC,
    createSwitchboard,
    updateSwitchboard,
    deleteElectricalNode,
    allocateToDdc,
    savePowerConnection,
    saveSwitchboardClassification,
  };
}
